<template>
  <GcsaLogo
    :href="href || undefined"
    :title="title || undefined"
    :tagline="taglineValue"
    :show-tagline="showTaglineParsed"
    :show-wordmark="showWordmarkParsed"
    :size="normalizedSize"
    :tone="normalizedTone"
    :theme="resolvedTheme"
    :layout="normalizedLayout"
    :root-class="logoThemeClass"
  />
</template>

<script setup lang="ts">
/**
 * Web Component root for <gcsa-logo>
 *   <gcsa-logo theme="dark" title="GCSA" tagline="Finger"></gcsa-logo>
 *
 * Passes resolved theme into the Vue logo + an inner theme class so iOS
 * does not rely on `:host([theme])` style invalidation alone.
 */
import { computed, toRef } from 'vue';
import GcsaLogo from './GcsaLogo.vue';
import { useCeHostTheme } from '../composables/useCeHostTheme';
import type { GcsaLogoLayout, GcsaLogoSize, GcsaLogoTone } from './GcsaLogo.vue';

const props = withDefaults(
  defineProps<{
    href?: string;
    title?: string;
    tagline?: string;
    showTagline?: string | boolean;
    showWordmark?: string | boolean;
    size?: string;
    tone?: string;
    theme?: string;
    layout?: string;
  }>(),
  {
    href: '',
    title: '',
    tagline: undefined,
    showTagline: true,
    showWordmark: true,
    size: 'md',
    tone: 'auto',
    theme: '',
    layout: 'row'
  }
);

const { resolvedTheme } = useCeHostTheme(toRef(props, 'theme'));

const parseBool = (value: string | boolean | undefined, fallback: boolean) => {
  if (value === undefined || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  if (value === 'false' || value === '0') return false;
  return true;
};

const showTaglineParsed = computed(() => parseBool(props.showTagline, true));
const showWordmarkParsed = computed(() => parseBool(props.showWordmark, true));

const taglineValue = computed(() => {
  if (props.tagline === undefined) return undefined;
  return props.tagline;
});

const SIZE_SET = new Set(['sm', 'md', 'lg']);
const TONE_SET = new Set(['auto', 'on-dark', 'on-light']);
const LAYOUT_SET = new Set(['row', 'stack']);

const normalizedSize = computed<GcsaLogoSize>(() =>
  SIZE_SET.has(props.size || '') ? (props.size as GcsaLogoSize) : 'md'
);
const normalizedTone = computed<GcsaLogoTone>(() =>
  TONE_SET.has(props.tone || '') ? (props.tone as GcsaLogoTone) : 'auto'
);
const normalizedLayout = computed<GcsaLogoLayout>(() =>
  LAYOUT_SET.has(props.layout || '') ? (props.layout as GcsaLogoLayout) : 'row'
);

const logoThemeClass = computed(() =>
  resolvedTheme.value === 'light' ? 'gcsa-logo-theme--light' : 'gcsa-logo-theme--dark'
);
</script>

<style>
@import '../css/logo.css';
</style>
